import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, ArrowUpRight, Zap } from "lucide-react";
import { api } from "@/lib/api";

const formatPrice = (p) =>
  p >= 1000
    ? p.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : p.toFixed(2);

const CriticalCard = ({ commodity, ticker }) => {
  const has = Boolean(ticker);
  const positive = has && ticker.change_pct >= 0;
  const isLive = has && ticker.source && ticker.source !== "reference";
  return (
    <Link
      to={`/commodities/${commodity.slug}`}
      className="bg-white border border-border p-5 flex flex-col gap-3 group hover:border-bb-navy transition-colors"
      data-testid={`critical-card-${commodity.slug}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-archivo font-bold text-bb-slate text-[11px] tracking-widest uppercase">
          {ticker?.symbol || commodity.name.slice(0, 2).toUpperCase()}
        </span>
        <div className="flex items-center gap-1.5">
          {isLive ? (
            <span
              className="inline-flex items-center gap-1 bb-tag-positive text-[9px] font-mono px-1.5 py-0.5"
              data-testid={`critical-card-${commodity.slug}-live-badge`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-bb-boreal animate-pulse" />
              LIVE
            </span>
          ) : has ? (
            <span
              className="inline-flex items-center gap-1 bb-tag-neutral text-[9px] font-mono px-1.5 py-0.5"
              data-testid={`critical-card-${commodity.slug}-ref-badge`}
            >
              REF
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 bb-tag-negative text-[9px] font-mono px-1.5 py-0.5">
            <Zap size={9} strokeWidth={2.5} /> Critical
          </span>
        </div>
      </div>
      <div>
        <div className="font-archivo font-bold text-bb-navy text-xl tracking-tight leading-none">
          {commodity.name}
        </div>
        <div className="text-[11px] font-mono text-bb-slate mt-1">
          {commodity.category}
        </div>
      </div>
      {has ? (
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-archivo font-bold text-bb-navy text-2xl leading-none">
              {formatPrice(ticker.price)}
            </span>
            <span className="text-[10px] font-mono text-bb-slate">
              {ticker.unit}
            </span>
          </div>
          <div
            className={`mt-1.5 inline-flex items-center gap-1 font-mono text-xs ${positive ? "text-bb-boreal" : "text-bb-copper"}`}
            data-testid={`critical-card-${commodity.slug}-change`}
          >
            {positive ? (
              <ArrowUp size={11} strokeWidth={2.5} />
            ) : (
              <ArrowDown size={11} strokeWidth={2.5} />
            )}
            {positive ? "+" : ""}
            {ticker.change_pct.toFixed(2)}%
          </div>
        </div>
      ) : (
        <div className="text-xs font-mono text-bb-slate">
          Not currently ticked.
        </div>
      )}
      <div className="mt-auto pt-3 border-t border-border flex items-center justify-between">
        <span className="text-[10px] font-mono text-bb-slate uppercase tracking-widest">
          View brief
        </span>
        <ArrowUpRight
          size={13}
          className="text-bb-navy group-hover:text-bb-copper transition-colors"
        />
      </div>
    </Link>
  );
};

export default function CriticalMineralsDashboard() {
  const [commodities, setCommodities] = useState([]);
  const [ticker, setTicker] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/commodities", { params: { critical: true } }),
      api.get("/market-ticker"),
    ])
      .then(([c, t]) => {
        if (!mounted) return;
        if (Array.isArray(c.data)) setCommodities(c.data);
        if (Array.isArray(t.data)) setTicker(t.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const tickerBySlug = useMemo(
    () => Object.fromEntries(ticker.map((t) => [t.commoditySlug, t])),
    [ticker],
  );

  if (commodities.length === 0) return null;

  return (
    <section
      className="py-14 lg:py-20 border-b border-border bg-bb-offwhite"
      data-testid="critical-minerals-dashboard"
    >
      <div className="bb-container">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-6">
          <div>
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-bb-copper" />
              <span className="bb-overline text-bb-copper">
                Critical Minerals · Live Dashboard
              </span>
            </div>
            <h2 className="font-archivo text-3xl lg:text-4xl text-bb-navy mt-3 font-bold tracking-tight">
              Canada&rsquo;s strategic supply, at a glance.
            </h2>
            <p className="mt-3 text-sm lg:text-base text-bb-slate max-w-2xl leading-relaxed">
              The five critical commodities where Canadian supply is most
              consequential to allied industrial policy — priced daily, tracked
              from mine to gigafactory.
            </p>
          </div>
          <Link
            to="/commodities"
            className="bb-link text-xs font-archivo uppercase tracking-widest text-bb-navy inline-flex items-center gap-1"
            data-testid="critical-view-all"
          >
            All commodities <ArrowUpRight size={14} strokeWidth={2.4} />
          </Link>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
          data-testid="critical-minerals-grid"
        >
          {commodities.map((c) => (
            <CriticalCard
              key={c.id}
              commodity={c}
              ticker={tickerBySlug[c.slug]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
